// src/pages/admin-entitlements/Usage.js
// Usage — the token-consumption view at /admin/entitlements/usage. Read-only:
// a trailing-30-day daily chart plus CSV export. No per-feature breakdown and
// no PostHog integration — both explicitly out of scope (see
// docs/superpowers/specs/2026-07-02-entitlements-usage-page-design.md).
//
// Raw chart.js, not react-chartjs-2 (not a dependency) — mirrors Admin.js's
// summaryChartInstance pattern: a canvas ref, Chart.register() once at module
// load, `new Chart(...)` in a useEffect, destroy-on-cleanup.
import { useEffect, useRef } from "react";
import { inject, observer } from "mobx-react";
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Legend,
  Title,
  Tooltip,
  Filler,
} from "chart.js";

Chart.register(
  LineController, LineElement, PointElement, CategoryScale, LinearScale,
  Legend, Title, Tooltip, Filler
);

const PLEX_NAVY = "#1e3a5f";
const PLEX_TEAL = "#0f8a96";

export const buildCsv = (series) => {
  const header = "date,input_tokens,output_tokens";
  const rows = series.map((d) => `${d.date},${d.input_tokens},${d.output_tokens}`);
  return [header, ...rows].join("\n");
};

const downloadCsv = (series) => {
  const csv = buildCsv(series);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `token-usage-${series[0]?.date || "export"}_to_${series[series.length - 1]?.date || ""}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const formatNumber = (n) => n.toLocaleString();

const Usage = ({ usageStore }) => {
  useEffect(() => { usageStore.load(); }, [usageStore]);

  const canvasRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || usageStore.series.length === 0) return;

    const labels = usageStore.series.map((d) => d.date.slice(5)); // "MM-DD"
    const inputData = usageStore.series.map((d) => d.input_tokens);
    const outputData = usageStore.series.map((d) => d.output_tokens);

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    chartInstance.current = new Chart(canvasRef.current, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Input tokens",
            data: inputData,
            borderColor: PLEX_NAVY,
            backgroundColor: "rgba(30, 58, 95, 0.08)",
            fill: true,
            tension: 0.25,
            pointRadius: 2,
            borderWidth: 2,
          },
          {
            label: "Output tokens",
            data: outputData,
            borderColor: PLEX_TEAL,
            backgroundColor: "rgba(15, 138, 150, 0.08)",
            fill: true,
            tension: 0.25,
            pointRadius: 2,
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: true, position: "bottom" },
          title: { display: false },
        },
        scales: {
          y: { beginAtZero: true, title: { display: true, text: "Tokens" } },
          x: { title: { display: true, text: "Date" } },
        },
      },
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [usageStore.series]);

  const totalInput = usageStore.series.reduce((sum, d) => sum + d.input_tokens, 0);
  const totalOutput = usageStore.series.reduce((sum, d) => sum + d.output_tokens, 0);
  const dayCount = usageStore.series.length || 1;
  const dailyAverage = Math.round((totalInput + totalOutput) / dayCount);

  return (
    <>
      <div className="page-hero">
        <div>
          <div className="adminEnt-eyebrow section-eyebrow">Usage</div>
          <h1 className="serif hero-display">Token consumption, <em>trailing 30 days</em>.</h1>
          <p className="lede">
            Daily input/output token totals for this tenant. Numbers, not
            dollars — no pricing table exists yet, so this is the honest proxy.
          </p>
        </div>
        {usageStore.series.length > 0 && (
          <div className="page-hero-actions">
            <button
              className="btn primary button-primary"
              onClick={() => downloadCsv(usageStore.series)}
            >
              Download CSV
            </button>
          </div>
        )}
      </div>

      {usageStore.error && <div className="err">Error: {usageStore.error}</div>}
      {usageStore.loading && <div className="empty">Loading…</div>}

      {!usageStore.loading && !usageStore.error && (
        <>
          <div className="cards-row cols-3">
            <div className="card brand stone-card">
              <div className="card-eyebrow">Input tokens (30d)</div>
              <div className="metric">{formatNumber(totalInput)}</div>
              <div className="metric-sub">sum across all users</div>
            </div>
            <div className="card stone-card">
              <div className="card-eyebrow">Output tokens (30d)</div>
              <div className="metric">{formatNumber(totalOutput)}</div>
              <div className="metric-sub">sum across all users</div>
            </div>
            <div className="card stone-card">
              <div className="card-eyebrow">Daily average</div>
              <div className="metric">{formatNumber(dailyAverage)}</div>
              <div className="metric-sub">tokens per day, both directions</div>
            </div>
          </div>

          <div className="card stone-card adminEnt-usageChartCard">
            <canvas ref={canvasRef} role="img" aria-label="Daily token consumption, trailing 30 days" />
          </div>
        </>
      )}
    </>
  );
};

export default inject("usageStore")(observer(Usage));
